import { useState, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Phone, Mail } from "lucide-react";

const CONTACT_ITEMS = [
  {
    id: "phone",
    label: "Appeler",
    value: "076 578 34 08",
    href: "tel:+41765783408",
    icon: Phone,
    external: false,
    hoverAnimation: "shake",
  },
  {
    id: "email",
    label: "Email",
    value: "julien@damore.ch",
    href: "mailto:julien@damore.ch",
    icon: Mail,
    external: false,
    hoverAnimation: "pulse",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    value: "Julien D'Amore",
    href: "https://www.linkedin.com/in/julien-d-amore-a8950697/",
    iconSvg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
    ),
    external: true,
    hoverAnimation: "glow",
  },
  {
    id: "instagram",
    label: "Instagram",
    value: "@julien.damore",
    href: "https://www.instagram.com/julien.damore",
    iconSvg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
    ),
    external: true,
    hoverAnimation: "glow",
  },
  {
    id: "facebook",
    label: "Facebook",
    value: "Julien D'Amore",
    href: "https://www.facebook.com/profile.php?id=61576821341276",
    iconSvg: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
    ),
    external: true,
    hoverAnimation: "glow",
  },
];

// 3D Tilt Card Component
function TiltCard({ children, href, external, className, testId, index }) {
  const ref = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const mouseXSpring = useSpring(x, { stiffness: 500, damping: 50 });
  const mouseYSpring = useSpring(y, { stiffness: 500, damping: 50 });
  
  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["8deg", "-8deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-8deg", "8deg"]);
  
  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };
  
  const handleMouseLeave = () => {
    setIsHovered(false);
    x.set(0);
    y.set(0);
  };

  return (
    <motion.a
      ref={ref}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.15 * index, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      style={{
        rotateX: isHovered ? rotateX : 0,
        rotateY: isHovered ? rotateY : 0,
        transformStyle: "preserve-3d",
      }}
      whileTap={{ scale: 0.97 }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      className={className}
      data-testid={testId}
    >
      {children}
      
      {/* Glow effect on hover */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: "radial-gradient(circle at center, rgba(74,144,255,0.15) 0%, transparent 70%)",
        }}
      />
      
      {/* Animated border gradient */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <div 
          className="absolute inset-[-2px] rounded-2xl"
          style={{
            background: isHovered 
              ? "linear-gradient(135deg, rgba(74,144,255,0.4) 0%, transparent 50%, rgba(74,144,255,0.2) 100%)"
              : "transparent",
            mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            maskComposite: "exclude",
            WebkitMaskComposite: "xor",
            padding: "1px",
          }}
        />
      </motion.div>
    </motion.a>
  );
}

// Animated Icon Component
function AnimatedIcon({ icon: Icon, iconSvg, animation, isHovered }) {
  const variants = {
    shake: {
      rotate: isHovered ? [0, -10, 10, -10, 10, 0] : 0,
      transition: { duration: 0.5, repeat: isHovered ? Infinity : 0, repeatDelay: 1 }
    },
    pulse: {
      scale: isHovered ? [1, 1.2, 1] : 1,
      transition: { duration: 0.6, repeat: isHovered ? Infinity : 0, repeatDelay: 0.5 }
    },
    glow: {
      filter: isHovered ? ["brightness(1)", "brightness(1.5)", "brightness(1)"] : "brightness(1)",
      transition: { duration: 1, repeat: isHovered ? Infinity : 0 }
    }
  };

  return (
    <motion.div
      className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-[#B07D62]/[0.15] to-[#B07D62]/[0.05] border border-[#B07D62]/[0.2] flex items-center justify-center text-[#B07D62] relative overflow-hidden"
      animate={variants[animation]}
      whileHover={{ scale: 1.1 }}
      style={{ transformStyle: "preserve-3d", transform: "translateZ(20px)" }}
    >
      {/* Inner glow */}
      <motion.div
        className="absolute inset-0 rounded-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 0.6 : 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: "radial-gradient(circle at center, rgba(74,144,255,0.4) 0%, transparent 70%)",
        }}
      />
      
      {/* Ring animation */}
      {isHovered && (
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-[#B07D62]/30"
          initial={{ scale: 1, opacity: 0.8 }}
          animate={{ scale: 1.8, opacity: 0 }}
          transition={{ duration: 1, repeat: Infinity }}
        />
      )}
      
      <span className="relative z-10 scale-110 md:scale-125">
        {Icon ? <Icon size={22} strokeWidth={1.5} /> : iconSvg}
      </span>
    </motion.div>
  );
}

// Floating particles background
function FloatingParticles() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 10 + 15,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-[#B07D62]/20"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// Animated title with letter reveal - D'Amore in copper italic
function AnimatedTitle({ text }) {
  // Split text to highlight D'Amore
  const parts = text.split(/(D'Amore)/);
  
  return (
    <motion.h2
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="font-['Playfair_Display'] text-3xl md:text-5xl tracking-tight font-light text-[#FAFAF8] mb-4"
      data-testid="contact-title"
    >
      {parts.map((part, partIndex) => {
        const letters = part.split("");
        const isDamore = part === "D'Amore";
        
        return letters.map((letter, i) => (
          <motion.span
            key={`${partIndex}-${i}`}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            transition={{ delay: 0.03 * (partIndex * 10 + i), duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className={`inline-block ${isDamore ? 'text-[#B07D62] italic' : ''}`}
            style={{ whiteSpace: letter === " " ? "pre" : "normal" }}
          >
            {letter === " " ? "\u00A0" : letter}
          </motion.span>
        ));
      })}
    </motion.h2>
  );
}

export default function ContactSection() {
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <div
      className="py-24 md:py-32 relative overflow-hidden"
      data-testid="contact-section"
      style={{ background: "rgba(5,5,5,0.95)" }}
    >
      {/* Floating particles */}
      <FloatingParticles />
      
      {/* Ambient glow */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] pointer-events-none"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.03, 0.06, 0.03],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background: "radial-gradient(ellipse at center, rgba(74,144,255,0.15) 0%, transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-label mb-3"
          >
            Contact
          </motion.p>
          
          {/* Animated title */}
          <AnimatedTitle text="Julien D'Amore" />
          
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-base text-[#FAFAF8]/80 leading-relaxed mb-12"
          >
            N'hésitez pas à me contacter pour discuter de votre projet immobilier.
          </motion.p>

          {/* Contact cards - Row 1: Phone & Email, Row 2: Social networks */}
          <div className="flex flex-col items-center gap-4 md:gap-6" style={{ perspective: "1000px" }}>
            {/* Row 1 - Phone & Email (2 cards) */}
            <div className="flex justify-center gap-3 md:gap-6">
              {CONTACT_ITEMS.slice(0, 2).map(({ id, label, value, href, icon, iconSvg, external, hoverAnimation }, i) => (
                <TiltCard
                  key={id}
                  href={href}
                  external={external}
                  index={i}
                  testId={`contact-${id}`}
                  className="relative glass-panel rounded-2xl p-3 md:p-6 flex flex-col items-center justify-center gap-2 md:gap-4 cursor-pointer transition-colors duration-500 hover:bg-white/[0.04] group w-[110px] h-[110px] md:w-[180px] md:h-[180px]"
                >
                  <div
                    onMouseEnter={() => setHoveredCard(id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    className="flex flex-col items-center gap-2 md:gap-3"
                    style={{ transform: "translateZ(30px)" }}
                  >
                    <AnimatedIcon 
                      icon={icon} 
                      iconSvg={iconSvg} 
                      animation={hoverAnimation}
                      isHovered={hoveredCard === id}
                    />
                    <div className="text-center" style={{ transform: "translateZ(20px)" }}>
                      <motion.p 
                        className="text-[8px] md:text-[11px] tracking-[0.1em] uppercase text-[#415A77] mb-0.5 md:mb-1 transition-colors duration-300 group-hover:text-[#B07D62]/70"
                      >
                        {label}
                      </motion.p>
                      <motion.p 
                        className="text-[10px] md:text-sm text-[#FAFAF8] font-medium transition-all duration-300 group-hover:text-white text-center leading-tight"
                      >
                        {value}
                      </motion.p>
                    </div>
                  </div>
                </TiltCard>
              ))}
            </div>
            
            {/* Row 2 - Social networks (3 cards) */}
            <div className="flex justify-center gap-3 md:gap-6">
              {CONTACT_ITEMS.slice(2, 5).map(({ id, label, value, href, icon, iconSvg, external, hoverAnimation }, i) => (
                <TiltCard
                  key={id}
                  href={href}
                  external={external}
                  index={i + 2}
                  testId={`contact-${id}`}
                  className="relative glass-panel rounded-2xl p-3 md:p-6 flex flex-col items-center justify-center gap-2 md:gap-4 cursor-pointer transition-colors duration-500 hover:bg-white/[0.04] group w-[110px] h-[110px] md:w-[180px] md:h-[180px]"
                >
                  <div
                    onMouseEnter={() => setHoveredCard(id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    className="flex flex-col items-center gap-2 md:gap-3"
                    style={{ transform: "translateZ(30px)" }}
                  >
                    <AnimatedIcon 
                      icon={icon} 
                      iconSvg={iconSvg} 
                      animation={hoverAnimation}
                      isHovered={hoveredCard === id}
                    />
                    <div className="text-center" style={{ transform: "translateZ(20px)" }}>
                      <motion.p 
                        className="text-[8px] md:text-[11px] tracking-[0.1em] uppercase text-[#415A77] mb-0.5 md:mb-1 transition-colors duration-300 group-hover:text-[#B07D62]/70"
                      >
                        {label}
                      </motion.p>
                      <motion.p 
                        className="text-[10px] md:text-sm text-[#FAFAF8] font-medium transition-all duration-300 group-hover:text-white text-center leading-tight"
                      >
                        {value}
                      </motion.p>
                    </div>
                  </div>
                </TiltCard>
              ))}
            </div>
          </div>
        </div>

        {/* Footer with hover effect */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-24 pt-8 border-t border-white/[0.05] flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-[#415A77]/50"
          data-testid="footer"
        >
          <motion.p 
            whileHover={{ color: "rgba(161, 161, 170, 0.8)" }}
            transition={{ duration: 0.3 }}
          >
            &copy; {new Date().getFullYear()} Julien <span className="text-[#B07D62] italic">D'Amore</span>. Tous droits réservés.
          </motion.p>
          
          <motion.a
            href="https://www.lynk.marketing"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#415A77]/50 hover:text-[#B07D62]/70 transition-colors duration-300"
            whileHover={{ scale: 1.02 }}
            data-testid="footer-lynk"
          >
            Création site web: <span className="font-medium">Lynk</span> - Agence digitale 360°
          </motion.a>
          
          <motion.img 
            src="/logo-jd.png" 
            alt="JD" 
            className="h-14 w-auto opacity-60"
            whileHover={{ opacity: 1, scale: 1.05 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      </div>
    </div>
  );
}
